import { useState, useRef, useEffect, useCallback } from "react";
import { formatChatDate } from "@/utils/chatDate";
import ReactMarkdown from "react-markdown";
import StatusBadge from "@/components/StatusBadge";
import { chatService } from "@/services/chatService";
import { ArrowRightFromLine, TriangleAlert } from "lucide-react";
import useUserStore from "@/store/userStore";
import {
  buildStreamPayload,
  buildStreamState,
  buildUserStreamMessage,
  createAguiIds,
  streamTrackingAguiRun,
} from "@/features/chat-agui/aguiStream";
import { mapAndEnrichChatMessages } from "@/features/chat-agui/mapChatMessages";
import { sanitizeStreamText } from "@/features/chat-agui/sanitizeStreamText";
import AguiMessageBlocks from "@/features/chat-agui/AguiMessageBlocks";
import {
  enrichUiWithTrackingMap,
  filterChatTrackingUi,
  createEphemeralTrackingThreadId,
  hasTrackingMapComponent,
  reconcileTextWithTrackingMap,
} from "@/features/chat-agui/enrichTrackingMapUi";
import {
  buildShipmentScopedForwardedProps,
  readShipmentChatCache,
  readShipmentHistoryCache,
  resolveShipmentBookingCode,
  resolveShipmentId,
  writeShipmentChatCache,
  writeShipmentHistoryCache,
} from "@/utils/shipmentChatUtils";
import {
  buildTrackingPagePath,
  isTrackingMapUiAction,
} from "@/utils/trackingMapNavigation";
import { useNavigate } from "react-router-dom";
import TrackingTypingBubble from "@/pages/chat/chat/TrackingTypingBubble";
import CollapsibleAgentText from "@/pages/chat/chat/CollapsibleAgentText";
import { chatMarkdownComponents, chatRemarkPlugins } from "@/utils/chatHelpers";
import ShipmentChatSkeleton from "@/features/chat-agui/ShipmentChatSkeleton";
import ChatConversationSkeleton from "@/features/chat-agui/ChatConversationSkeleton";
import MobileIconButton from "@/components/MobileIconButton";
import FloatingChatInput from "@/components/chat/FloatingChatInput";
import FloatingChatComposer from "@/components/chat/FloatingChatComposer";
import { useChatDraft } from "@/hooks/useChatDraft";
import { useIntersectionLoadMore } from "@/hooks/useIntersectionLoadMore";
import { usePreserveScrollOnAppend } from "@/hooks/usePreserveScrollOnAppend";
import {
  resolveAgentUiForDisplay,
  type ResolveAgentUiOptions,
} from "@/features/chat-agui/aguiDisplayUtils";


const MESSAGES_PAGE_SIZE = 50;

function unwrapMessagesResponse(res: unknown): any[] {
  if (Array.isArray(res)) return res;
  const r = res as Record<string, unknown> | null;
  if (Array.isArray(r?.data)) return r.data as any[];
  if (Array.isArray(r?.messages)) return r.messages as any[];
  if (r?.data && typeof r.data === "object") {
    const inner = r.data as Record<string, unknown>;
    if (Array.isArray(inner.messages)) return inner.messages as any[];
  }
  return [];
}

function resolveShipmentHistoryChatId(shipment: any) {
  const raw = shipment?._raw ?? shipment ?? {};
  return (
    raw.chat_id ??
    raw.chatId ??
    raw.conversation_id ??
    raw.conversationId ??
    null
  );
}

/** Shipment panel thread — isolated from /chats list and tracking page */
function shipmentPanelThreadId(shipment: any) {
  const id = shipment?.id ?? shipment?._raw?.id;
  return id ? `shp_panel_${id}` : null;
}

function formatMessageTime(ts: string | Date | undefined) {
  if (!ts) return "";
  try {
    return new Date(ts).toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}

export default function ShipmentChatPanel({
  shipment,
  onOpenDetails,
  onBack,
}: {
  shipment: any;
  onOpenDetails: () => void;
  onBack?: () => void;
}) {
  const navigate = useNavigate();
  const { user } = useUserStore();
  const shipmentKey = shipment?.id ?? shipment?._raw?.id;
  const panelThreadId = shipmentPanelThreadId(shipment);
  const { input, setInput, clearDraft } = useChatDraft(panelThreadId);
  const [historyMessages, setHistoryMessages] = useState<any[]>(() =>
    shipmentKey ? readShipmentHistoryCache(shipmentKey) : [],
  );
  const [sessionTurns, setSessionTurns] = useState<any[]>(() =>
    shipmentKey ? readShipmentChatCache(shipmentKey) : [],
  );
  const sessionCacheReadyRef = useRef(false);
  const [loading, setLoading] = useState(false);
  const [hasMoreHistory, setHasMoreHistory] = useState(false);
  const [loadingMoreHistory, setLoadingMoreHistory] = useState(false);
  const [sending, setSending] = useState(false);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.innerWidth <= 768,
  );

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);
  const bottomRef = useRef<HTMLDivElement>(null);
  const messagesScrollRef = useRef<HTMLDivElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  const historyChatId = resolveShipmentHistoryChatId(shipment);

  useEffect(() => {
    sessionCacheReadyRef.current = false;
    const cachedHistory = shipmentKey ? readShipmentHistoryCache(shipmentKey) : [];
    setHistoryMessages(cachedHistory);
    setHasMoreHistory(cachedHistory.length >= MESSAGES_PAGE_SIZE);
    setSessionTurns(shipmentKey ? readShipmentChatCache(shipmentKey) : []);
    setLoading(false);
    setLoadingMoreHistory(false);
    abortRef.current?.abort();
    sessionCacheReadyRef.current = true;
  }, [shipmentKey]);

  const enrichHistoryRows = useCallback(
    async (rows: any[]) => {
      if (!historyChatId || !rows.length) return [];
      const { ipTimezone, ipOffsetMinutes } = useUserStore.getState();
      return mapAndEnrichChatMessages(rows, historyChatId, {
        userId: user?.id || user?._id,
        timeZone: ipTimezone,
        offsetMinutes: ipOffsetMinutes,
        includeTrackingState: true,
      });
    },
    [historyChatId, user?.id, user?._id],
  );

  useEffect(() => {
    if (!historyChatId) {
      setHistoryMessages([]);
      setHasMoreHistory(false);
      setLoading(false);
      return;
    }

    let cancelled = false;
    const cached = shipmentKey ? readShipmentHistoryCache(shipmentKey) : [];

    if (cached.length) {
      setHistoryMessages(cached);
      setHasMoreHistory(cached.length >= MESSAGES_PAGE_SIZE);
      setLoading(false);
      return;
    }

    setLoading(true);

    (async () => {
      try {
        const res = await chatService.getChatMessages(historyChatId, MESSAGES_PAGE_SIZE);
        const rows = unwrapMessagesResponse(res);
        const enriched = await enrichHistoryRows(rows);
        if (!cancelled) {
          setHistoryMessages(enriched);
          setHasMoreHistory(rows.length >= MESSAGES_PAGE_SIZE);
          if (shipmentKey) writeShipmentHistoryCache(shipmentKey, enriched);
        }
      } catch {
        if (!cancelled) setHistoryMessages([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [historyChatId, shipmentKey, enrichHistoryRows]);

  const loadOlderHistory = useCallback(async () => {
    if (!historyChatId || loadingMoreHistory || !hasMoreHistory || historyMessages.length === 0) {
      return;
    }

    const oldestId = historyMessages[0]?.id;
    if (!oldestId) return;

    setLoadingMoreHistory(true);
    try {
      const res = await chatService.getChatMessages(
        historyChatId,
        MESSAGES_PAGE_SIZE,
        oldestId,
      );
      const rows = unwrapMessagesResponse(res);
      if (!rows.length) {
        setHasMoreHistory(false);
        return;
      }

      const enriched = await enrichHistoryRows(rows);
      setHistoryMessages((prev) => {
        const seen = new Set(prev.map((m) => m.id));
        const older = enriched.filter((m) => !seen.has(m.id));
        const merged = [...older, ...prev];
        if (shipmentKey) writeShipmentHistoryCache(shipmentKey, merged);
        return merged;
      });
      setHasMoreHistory(rows.length >= MESSAGES_PAGE_SIZE);
    } catch {
      setHasMoreHistory(false);
    } finally {
      setLoadingMoreHistory(false);
    }
  }, [
    historyChatId,
    historyMessages,
    hasMoreHistory,
    loadingMoreHistory,
    enrichHistoryRows,
    shipmentKey,
  ]);

  usePreserveScrollOnAppend({
    containerRef: messagesScrollRef,
    itemCount: historyMessages.length + sessionTurns.length,
    isLoadingMore: loadingMoreHistory,
  });

  const historyTopSentinelRef = useIntersectionLoadMore({
    enabled: hasMoreHistory && !loading,
    isLoading: loadingMoreHistory,
    onLoadMore: loadOlderHistory,
    rootRef: messagesScrollRef,
    observeKey: historyMessages.length,
    edge: "top",
    resetKey: historyChatId,
  });

  useEffect(() => {
    if (!shipmentKey || !sessionCacheReadyRef.current) return;
    writeShipmentChatCache(shipmentKey, sessionTurns);
  }, [sessionTurns, shipmentKey]);

  const initialHistoryScrolledRef = useRef(false);

  useEffect(() => {
    initialHistoryScrolledRef.current = false;
  }, [shipmentKey]);

  useEffect(() => {
    if (!loading && historyMessages.length > 0 && !initialHistoryScrolledRef.current) {
      initialHistoryScrolledRef.current = true;
      bottomRef.current?.scrollIntoView({ behavior: "auto" });
    }
  }, [loading, historyMessages.length]);

  useEffect(() => {
    if (sessionTurns.length > 0 || sending) {
      bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [sessionTurns, sending]);

  const handleUiAction = useCallback(
    ({ actionId, value }: { actionId: string; value?: string }) => {
      if (isTrackingMapUiAction(actionId)) {
        navigate(buildTrackingPagePath(value));
      }
    },
    [navigate],
  );

  const sendMessage = useCallback(async () => {
    const trimmed = input.trim();
    if (!trimmed || sending || !panelThreadId) return;

    const bookingCode = resolveShipmentBookingCode(shipment);
    const shipmentId = resolveShipmentId(shipment);

    abortRef.current?.abort();
    abortRef.current = new AbortController();

    const userId = user?.id || user?._id || "anonymous";
    const { messageId, runId } = createAguiIds();
    const aguiThreadId = createEphemeralTrackingThreadId();
    const streamMessage =
      bookingCode && /track/i.test(trimmed)
        ? `Track shipment ${String(bookingCode).replace(/^#/, "")} (${shipment?.vehicle ?? "shipment"})`
        : trimmed;

    const userTurn = {
      id: `user_${Date.now()}`,
      role: "user",
      text: trimmed,
      timestamp: new Date().toISOString(),
    };
    const agentId = `agent_${Date.now()}`;
    const agentTurn = {
      id: agentId,
      role: "agent",
      text: "",
      uiComponents: [],
      isStreaming: true,
      timestamp: new Date().toISOString(),
    };

    setSessionTurns((prev) => [...prev, userTurn, agentTurn]);
    clearDraft();
    setSending(true);

    const patchAgent = (patch: Record<string, unknown>) => {
      setSessionTurns((prev) =>
        prev.map((m) => (m.id === agentId ? { ...m, ...patch } : m)),
      );
    };

    try {
      const { ipTimezone, ipOffsetMinutes } = useUserStore.getState();
      const timeZone =
        ipTimezone ||
        localStorage.getItem("ip_timezone") ||
        Intl.DateTimeFormat().resolvedOptions().timeZone;
      const storedOffset = localStorage.getItem("ip_offset_minutes");
      const offsetMinutes =
        ipOffsetMinutes !== null && ipOffsetMinutes !== undefined
          ? ipOffsetMinutes
          : storedOffset !== null
            ? Number(storedOffset)
            : -new Date().getTimezoneOffset();

      const sessionId = historyChatId ?? panelThreadId ?? aguiThreadId;

      const payload = buildStreamPayload({
        threadId: aguiThreadId,
        runId,
        messages: [buildUserStreamMessage(streamMessage, userId, messageId)],
        state: buildStreamState({
          sessionId,
          chatId: historyChatId ?? panelThreadId,
          newSession: false,
          timeZone,
          offsetMinutes,
          shipmentChatScope: true,
          currentShipmentId: shipmentId,
        }),
        forwardedProps: buildShipmentScopedForwardedProps(shipment),
      });

      let textAcc = "";

      await streamTrackingAguiRun(
        payload,
        {
          onTextDelta: (full) => {
            textAcc = full;
            patchAgent({ text: sanitizeStreamText(full) });
          },
          // Defer AG-UI until run finishes + map enrichment (avoids summary flash before map)
          onUiComponents: () => {},
          onRunFinished: async ({ text: finishedText, uiComponents }) => {
            // #506: finalize the streamed answer and stop the streaming state
            // BEFORE the async map enrichment, so the extra track-by-code
            // round-trip can't wipe/re-show (or duplicate) the already-rendered
            // text. The map card is added to the same message below; the text is
            // only rewritten if a map contradicts "not found" prose.
            patchAgent({ text: sanitizeStreamText(finishedText || textAcc), isStreaming: false });

            let ui = uiComponents ?? [];
            try {
              ui = await enrichUiWithTrackingMap(ui, streamMessage, {
                threadId: aguiThreadId,
                userId,
                timeZone,
                offsetMinutes,
                shipmentBookingId: bookingCode,
                shipmentId,
              });
              ui = filterChatTrackingUi(ui, streamMessage);
            } catch {
              /* keep stream ui */
            }
            let text = sanitizeStreamText(finishedText || textAcc);
            if (hasTrackingMapComponent(ui)) {
              text = reconcileTextWithTrackingMap(text, {
                bookingCode,
                vehicle: shipment?.vehicle,
              });
            }
            const displayOpts: ResolveAgentUiOptions = {
              text,
              uiComponents: ui,
              userPrompt: trimmed,
            };
            const resolvedUi = resolveAgentUiForDisplay(displayOpts);
            patchAgent({
              text,
              uiComponents: resolvedUi.uiComponents,
              isAgui: resolvedUi.isAgui,
              isStreaming: false,
            });

            if (historyChatId) {
              try {
                const res = await chatService.getChatMessages(historyChatId, 50);
                const rows = unwrapMessagesResponse(res);
                const { ipTimezone, ipOffsetMinutes } = useUserStore.getState();
                const enriched = await mapAndEnrichChatMessages(rows, historyChatId, {
                  userId: user?.id || user?._id,
                  timeZone: ipTimezone,
                  offsetMinutes: ipOffsetMinutes,
                  includeTrackingState: true,
                });
                if (enriched.length) {
                  setHistoryMessages(enriched);
                  writeShipmentHistoryCache(shipmentKey, enriched);
                  setSessionTurns([]);
                  writeShipmentChatCache(shipmentKey, []);
                }
              } catch {
                /* keep session + cache */
              }
            }
          },
          onRunError: () => {
            patchAgent({
              isStreaming: false,
              text: "Sorry, I couldn't complete that. Please try again.",
            });
          },
        },
        abortRef.current.signal,
      );
    } catch (err: any) {
      if (err?.name !== "AbortError") {
        patchAgent({
          isStreaming: false,
          text: "Something went wrong. Please try again.",
        });
      }
    } finally {
      setSending(false);
    }
  }, [input, sending, panelThreadId, shipment, user, historyChatId, shipmentKey]);

  /** Skeleton only on first load with no cached history or session */
  const showChatSkeleton =
    loading &&
    !!historyChatId &&
    historyMessages.length === 0 &&
    sessionTurns.length === 0;
  const displayRows = showChatSkeleton
    ? []
    : [...historyMessages, ...sessionTurns];

  // #501: date header = the day of the conversation (first timestamped message),
  // not today.
  const dateLabel = formatChatDate(
    displayRows.find((m: any) => m?.timestamp)?.timestamp
  ).toUpperCase();

  const renderAgentBlock = (msg: any) => {
    const hasUi = Array.isArray(msg.uiComponents) && msg.uiComponents.length > 0;
    const text = (msg.text ?? "").trim();
    const time = formatMessageTime(msg.timestamp);
    const showTyping = msg.isStreaming && !text;

    return (
      <div key={msg.id} className="flex justify-start">
        <div className="max-w-[85%] min-w-0">
          <div className="flex flex-col gap-3 min-w-0">
            {hasUi && !msg.isStreaming ? (
              <AguiMessageBlocks
                uiComponents={msg.uiComponents}
                onUiAction={handleUiAction}
                disabled={false}
                className="mt-0 space-y-3 min-w-0 w-full max-w-full overflow-visible"
              />
            ) : null}
            {showTyping ? <TrackingTypingBubble /> : null}
            {text && !msg.isStreaming ? (
              <div className="rounded-2xl px-4 py-3 text-[14px] leading-relaxed bg-white text-[#11121A] rounded-bl-sm border border-[#E4E7EC]">
                <div
                  className="text-[#1D2739] text-[14px] max-w-none break-words"
                  style={{ overflowWrap: "anywhere" }}
                >
                  {hasUi ? (
                    <CollapsibleAgentText
                      text={text}
                      components={chatMarkdownComponents}
                      minCharsToCollapse={200}
                    />
                  ) : (
                    <ReactMarkdown remarkPlugins={chatRemarkPlugins} components={chatMarkdownComponents}>
                      {text}
                    </ReactMarkdown>
                  )}
                </div>
              </div>
            ) : null}
            {text && msg.isStreaming ? (
              <div className="rounded-2xl px-4 py-3 text-[14px] leading-relaxed bg-white text-[#11121A] rounded-bl-sm border border-[#E4E7EC]">
                <div
                  className="text-[#1D2739] text-[14px] max-w-none break-words whitespace-pre-wrap"
                  style={{ overflowWrap: "anywhere" }}
                >
                  {text}
                </div>
              </div>
            ) : null}
          </div>
          {time ? (
            <p className="text-[11px] text-[#98A2B3] mt-0.5 px-1 text-left">{time}</p>
          ) : null}
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="flex-shrink-0 flex items-center justify-between gap-2 px-4 py-3 border-b border-[#E4E7EC] bg-white">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          {onBack ? (
            <MobileIconButton onClick={onBack} aria-label="Back to shipments">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path
                  d="M19 12H5M12 19L5 12L12 5"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </MobileIconButton>
          ) : null}
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <h2 className="text-[16px] font-[600] text-[#11121A] truncate">{shipment.vehicle}</h2>
            <StatusBadge status={shipment.status} className="max-w-[44vw] md:max-w-[220px] flex-shrink-0" />
          </div>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <h2 className="hidden md:block text-[14px] font-[500] text-[#0A0A0A]">Open details</h2>
          <button
            type="button"
            onClick={onOpenDetails}
            className="inline-flex items-center justify-center w-9 h-9 rounded-lg hover:bg-[#F0F2F5] transition-colors text-[#667185] flex-shrink-0"
            aria-label="Open shipment details"
          >
            <ArrowRightFromLine size={18} />
          </button>
        </div>
      </div>

      {shipment?._raw?.balance > 0 ? (
        <div className="flex-shrink-0 flex items-center justify-between px-4 py-3 bg-[#FBEAE9] border border-[#D0D5DD] w-[90%] mx-auto rounded-xl mt-4">
          <div className="flex items-start gap-2">
            <TriangleAlert size={18} color="#D92D20" className="mt-1" />
            <div>
              <p className="text-[14px] font-[700] text-[#BA110B]">
                Payment due · ${shipment?._raw?.balance?.toLocaleString()}
              </p>
              <p className="text-[13px] font-[400] text-[#737373]">
                Complete payment to keep your shipment on schedule.
              </p>
            </div>
          </div>
          <button
            type="button"
            className="flex-shrink-0 text-[12px] font-[500] text-[#0A0A0A] border border-[#D4D4D4] bg-white px-5 py-1 rounded-[8px] hover:bg-[#F9FAFB] transition-colors"
          >
            Pay Now
          </button>
        </div>
      ) : null}

      <div ref={messagesScrollRef} className="flex-1 min-h-0 overflow-y-auto px-4 py-4 md:px-8 md:py-6 space-y-4">
        {dateLabel ? (
          <div className="flex justify-center mb-4">
            <span className="text-[11px] font-[600] tracking-widest text-[#98A2B3]">
              {dateLabel}
            </span>
          </div>
        ) : null}

        {hasMoreHistory ? <div ref={historyTopSentinelRef} className="h-px" aria-hidden /> : null}
        {loadingMoreHistory ? <ChatConversationSkeleton rows={1} showAvatar={false} /> : null}

        {showChatSkeleton ? <ShipmentChatSkeleton /> : null}

        {!showChatSkeleton && displayRows.length === 0 ? (
          <p className="text-center text-[13px] text-[#98A2B3]">No messages yet.</p>
        ) : null}

        {!showChatSkeleton
          ? displayRows.map((msg) => {
          const isUser = msg.role === "user";
          if (isUser) {
            const time = formatMessageTime(msg.timestamp);
            return (
              <div key={msg.id} className="flex justify-end">
                <div className="max-w-[65%]">
                  <div className="rounded-2xl rounded-br-md px-4 py-3 text-[14px] leading-relaxed bg-[#F7F9FC] text-[#11121A] shadow-sm">
                    {msg.text}
                  </div>
                  {time ? (
                    <p className="text-[11px] text-[#98A2B3] mt-0.5 px-1 text-right">
                      {time}
                    </p>
                  ) : null}
                </div>
              </div>
            );
          }
          return renderAgentBlock(msg);
            })
          : null}
        <div ref={bottomRef} />
      </div>

      <FloatingChatComposer>
        <FloatingChatInput
          multiline={!isMobile}
          input={input}
          setInput={setInput}
          onSend={sendMessage}
          loading={sending}
          inputRef={inputRef}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey && !isMobile) {
              e.preventDefault();
              sendMessage();
            }
            if (e.key === "Enter" && isMobile) {
              e.preventDefault();
              sendMessage();
            }
          }}
          showUpload={false}
        />
      </FloatingChatComposer>
    </div>
  );
}
